import { clearLastSearch, getLastViewedEstab, getLastSearch, clearLastViewedEstab, TripType, DateSearchType, } from '@honeycomb/data';
import { getDetailsPageUrl } from '@honeycomb/routing';
import { getDateSummary } from './getDateSummary';
import { getSelectedOptionSummary } from './getSelectedOptionSummary';
import { addDay, getDayDiff, getLastDateOfMonth, toUTCDate } from './date-utils';
// Function to extract SingleAirport entries
const extractSingleAirports = (airports) => {
    const singleAirports = [];
    airports.forEach((airportGroup) => {
        if (airportGroup.__typename === 'SingleAirport') {
            singleAirports.push({
                code: airportGroup.code,
                title: airportGroup.title,
                country: airportGroup.country,
            });
        }
        else if (airportGroup.__typename === 'AirportGroup' && airportGroup.subAirports) {
            airportGroup.subAirports.forEach((subAirport) => {
                if (subAirport) {
                    singleAirports.push({
                        code: subAirport.code,
                        title: subAirport.title,
                        country: subAirport.country,
                    });
                }
            });
        }
    });
    if (singleAirports.length) {
        const uniqueSet = new Set(singleAirports.map((airport) => JSON.stringify(airport)));
        return Array.from(uniqueSet).map((entry) => JSON.parse(entry));
    }
    return singleAirports;
};
function getFlightDetails(param, airports) {
    var _a;
    if (param.tripType === TripType.Holiday && ((_a = param.departureAirports) === null || _a === void 0 ? void 0 : _a.length) && airports) {
        const airportList = extractSingleAirports(airports);
        const selectedAirport = airportList.filter((airport) => { var _a; return (_a = param.departureAirports) === null || _a === void 0 ? void 0 : _a.includes(airport.code); });
        return getSelectedOptionSummary(selectedAirport);
    }
    return '';
}
function updateSearchState(searchParam, estabId, estabName) {
    var _a, _b;
    if (!searchParam)
        return searchParam;
    let state = Object.assign({}, searchParam);
    if (searchParam.destinations && searchParam.destinations.length > 0) {
        const hotelDestination = {
            id: estabId,
            type: 1,
            title: estabName,
            subTitle: '',
            isCached: (_b = (_a = searchParam.destinations[0]) === null || _a === void 0 ? void 0 : _a.isCached) !== null && _b !== void 0 ? _b : false,
        };
        state = Object.assign(Object.assign({}, state), { destinations: [hotelDestination] });
    }
    else if (searchParam.destinationIds && searchParam.destinationIds.length > 0) {
        state = Object.assign(Object.assign({}, state), { hotelIds: [estabId], hotelTitle: estabName, destinationIds: null });
    }
    return state;
}
export function mapDetails(estab, searchParam, airports) {
    var _a, _b, _c;
    const param = searchParam;
    param.tripStateId = estab.tripStateId;
    const detailsPageUrl = getDetailsPageUrl(param, estab.estabId);
    const isMonthSearch = param.dateSearchType === DateSearchType.ChooseMonth;
    const data = {
        hotelName: estab.name,
        rating: estab.starRating,
        travelDate: getDateSummary(new Date(isMonthSearch ? (_a = param.wholeMonthStartDate) !== null && _a !== void 0 ? _a : '' : param.startDate), new Date(isMonthSearch ? (_b = param.wholeMonthEndDate) !== null && _b !== void 0 ? _b : '' : param.endDate), true),
        hotelImageUrl: `${estab.imageUrl}?width=89&height=85&crop=true&quality=80`,
        detailsPageHref: detailsPageUrl,
        flightDetails: getFlightDetails(param, airports),
        tripType: (_c = param.tripType) !== null && _c !== void 0 ? _c : TripType.Hotel,
        destinationDetails: param.tripType === TripType.Hotel ? estab.location : '',
    };
    return data;
}
export function useStoredRecentViewed(airportData) {
    var _a;
    let data = null;
    const estabData = getLastViewedEstab();
    const searchData = getLastSearch();
    const clearRecentViewStorage = () => {
        clearLastSearch();
        clearLastViewedEstab();
        data = null;
    };
    if (estabData && !searchData)
        clearLastViewedEstab();
    if (estabData && searchData) {
        const estabDetails = JSON.parse(estabData);
        const searchDetails = JSON.parse(searchData);
        if (getDayDiff(new Date().toISOString(), searchDetails.startDate) > 0 ||
            getDayDiff(new Date().toISOString(), (_a = searchDetails.wholeMonthEndDate) !== null && _a !== void 0 ? _a : '') > 0) {
            const updatedDetails = updateSearchState(searchDetails, estabDetails.estabId, estabDetails.name);
            data = mapDetails(estabDetails, updatedDetails, airportData === null || airportData === void 0 ? void 0 : airportData.airports);
        }
        else {
            clearRecentViewStorage();
        }
    }
    return [data, clearRecentViewStorage];
}
function getNextMonthDates() {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const nextMonthFirstDate = currentMonth === 11 ? new Date(currentYear + 1, 0, 1) : new Date(currentYear, currentMonth + 1, 1);
    const nextMonthLastDate = currentMonth === 11 ? new Date(currentYear + 1, 1, 0) : new Date(currentYear, currentMonth + 2, 0);
    return {
        startDate: nextMonthFirstDate.toISOString().split('T')[0],
        endDate: nextMonthLastDate.toISOString().split('T')[0],
    };
}
function updateDates(searchDetails, baseState, isCached) {
    var _a;
    const currentDateString = new Date().toISOString();
    const isChooseMonth = searchDetails.dateSearchType === DateSearchType.ChooseMonth;
    const isStartDateInPast = getDayDiff(currentDateString, searchDetails.startDate) < 1;
    let updatedDates = {
        startDate: searchDetails.startDate,
        endDate: searchDetails.endDate,
        wholeMonthStartDate: searchDetails.wholeMonthStartDate,
        wholeMonthEndDate: searchDetails.wholeMonthEndDate,
        dateSearchType: searchDetails.dateSearchType,
    };
    if (isChooseMonth) {
        if (!isCached) {
            updatedDates = {
                startDate: baseState.startDate,
                endDate: baseState.endDate,
                wholeMonthStartDate: '',
                wholeMonthEndDate: '',
                dateSearchType: DateSearchType.ChooseDate,
            };
        }
        else {
            updatedDates.startDate = '';
            updatedDates.endDate = '';
            if (getDayDiff(currentDateString, (_a = searchDetails.wholeMonthEndDate) !== null && _a !== void 0 ? _a : '') < 1) {
                const nextMonthDates = getNextMonthDates();
                updatedDates.wholeMonthStartDate = nextMonthDates.startDate;
                updatedDates.wholeMonthEndDate = nextMonthDates.endDate;
            }
        }
    }
    else if (isStartDateInPast) {
        updatedDates = {
            startDate: baseState.startDate,
            endDate: baseState.endDate,
            wholeMonthStartDate: '',
            wholeMonthEndDate: '',
            dateSearchType: DateSearchType.ChooseDate,
        };
    }
    return updatedDates;
}
const getDestinationDetails = (id, title, legacyType, isCacheEnabled = false, isCached = false) => {
    if (!id) {
        return { destinations: null, destinationIds: null, destinationTitle: '', hotelIds: null, hotelTitle: '' };
    }
    const destId = parseInt(id);
    if (isCacheEnabled) {
        return {
            destinations: [{ id: destId, type: legacyType, title: title !== null && title !== void 0 ? title : '', isCached }],
            destinationIds: null,
            destinationTitle: '',
            hotelIds: null,
            hotelTitle: '',
        };
    }
    return {
        destinations: null,
        destinationIds: legacyType === 1 ? null : [destId],
        destinationTitle: legacyType === 1 ? '' : title !== null && title !== void 0 ? title : '',
        hotelIds: legacyType !== 1 ? null : [destId],
        hotelTitle: legacyType !== 1 ? '' : title !== null && title !== void 0 ? title : '',
    };
};
const getDefaultSearch = (search) => {
    var _a;
    try {
        if (!search)
            return null;
        return (_a = JSON.parse(search)) !== null && _a !== void 0 ? _a : null;
    }
    catch (error) {
        console.error('Error on parsing default search:', error);
        return null;
    }
};
const getBaseState = (prepopulatedInput, isLastSearchAvailable) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { searchParam, id, title, legacyType, destination, duration, durationOffset, airports, adults, isCacheEnabled, isCached, } = prepopulatedInput;
    // if last search is available then we will not use default search
    const searchDestinations = isLastSearchAvailable ? null : getDefaultSearch(destination);
    const destId = id || ((_b = (_a = searchDestinations === null || searchDestinations === void 0 ? void 0 : searchDestinations.id) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : '');
    const destTitle = title || ((_c = searchDestinations === null || searchDestinations === void 0 ? void 0 : searchDestinations.title) !== null && _c !== void 0 ? _c : '');
    const destType = (_d = (legacyType || (searchDestinations === null || searchDestinations === void 0 ? void 0 : searchDestinations.type))) !== null && _d !== void 0 ? _d : 0;
    const isCachedValue = id ? isCached : (_e = searchDestinations === null || searchDestinations === void 0 ? void 0 : searchDestinations.isCached) !== null && _e !== void 0 ? _e : false;
    const travelduration = (_f = parseInt(duration)) !== null && _f !== void 0 ? _f : 7;
    const adultCount = (_g = parseInt(adults)) !== null && _g !== void 0 ? _g : 2;
    const { destinations, destinationIds, destinationTitle, hotelIds, hotelTitle } = getDestinationDetails(destId, destTitle, destType, isCacheEnabled, isCachedValue);
    // add default offset in current date
    const newDate = new Date();
    newDate.setDate(newDate.getDate() + durationOffset);
    const baseState = Object.assign(Object.assign({}, searchParam), { hotelIds,
        hotelTitle,
        destinations,
        destinationIds,
        destinationTitle, startDate: toUTCDate(newDate), endDate: toUTCDate(addDay(newDate, travelduration)), departureAirports: (_h = airports.split('|')) !== null && _h !== void 0 ? _h : null, rooms: [
            {
                adults: adultCount,
                childAges: [],
            },
        ], adults: adultCount });
    return baseState;
};
// This function is basically used for prefilled search component inputs there is priority for destination input as
// For destination/estab landing page priority will in following order
// 1: Umbraco/content data 2: Last search 3: Default [Derwent config]
// For home and other landing page priority will in following order
// 1: Last search 2: Umbraco/content data 3: Default [Derwent config]
export function updateLandingPageSearch(prepopulatedInput) {
    try {
        // get last search made by user
        const searchData = getLastSearch();
        // get baseState as umbraco/default data.
        // in case of last search, baseState will contain umbraco data for destination
        const baseState = getBaseState(prepopulatedInput, !!searchData);
        // if last search not available then return baseState which may be umbraco or default [derwent] data
        if (!searchData)
            return baseState;
        const { isCached, isDestinationOrEstabPage } = prepopulatedInput;
        const searchDetails = JSON.parse(searchData);
        const { startDate, endDate, wholeMonthStartDate, wholeMonthEndDate, dateSearchType } = updateDates(searchDetails, baseState, isCached);
        // check base destination is available or not.
        const isBaseDestination = !(baseState.destinations === null &&
            baseState.destinationIds === null &&
            baseState.hotelIds === null);
        // set destination based on priority as if destination//estab landing page then give priority to baseState
        // if not then last search. In case of home page and other landing page, case will be reversed.
        const updatedParam = isDestinationOrEstabPage && isBaseDestination ? baseState : searchDetails;
        return Object.assign(Object.assign({}, baseState), { destinations: updatedParam.destinations, hotelIds: updatedParam.hotelIds, hotelTitle: updatedParam.hotelTitle, destinationIds: updatedParam.destinationIds, destinationTitle: updatedParam.destinationTitle, startDate,
            endDate,
            wholeMonthEndDate,
            wholeMonthStartDate, duration: dateSearchType === DateSearchType.ChooseMonth ? searchDetails.duration : null, dateSearchType, adults: searchDetails.adults, childAges: searchDetails.childAges, rooms: searchDetails.rooms, departureAirports: searchDetails.departureAirports && searchDetails.departureAirports.length > 0
                ? searchDetails.departureAirports
                : baseState.departureAirports });
    }
    catch (error) {
        console.error('Error updating landingPage search:', error);
        return prepopulatedInput.searchParam;
    }
}
function getDealMonthAndYear(departureDate, offset) {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    const lastDateOfCurrentMonth = getLastDateOfMonth(currentDate);
    const departureDateObj = new Date(departureDate);
    // if departure date is pastDate then month year will be calendar month year
    if (departureDateObj < currentDate)
        return '';
    if (departureDateObj > lastDateOfCurrentMonth) {
        return `${departureDateObj.getMonth() + 1}-${departureDateObj.getFullYear()}`;
    }
    if (lastDateOfCurrentMonth > addDay(currentDate, offset)) {
        // Departure date is in the current month
        return `${currentMonth}-${currentYear}`;
    }
    const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
    const nextYear = currentMonth === 12 ? currentYear + 1 : currentYear;
    return `${nextMonth}-${nextYear}`;
}
function createEstabOffersRequest(baseState, request) {
    var _a, _b;
    return {
        adults: (_a = baseState.adults) !== null && _a !== void 0 ? _a : 2,
        airportCodes: (_b = baseState.departureAirports) !== null && _b !== void 0 ? _b : [],
        children: 0,
        duration: parseInt(request.duration),
        estabID: request.id,
        calendarMonthYear: '',
    };
}
export function getEstabOfferRequest(request) {
    var _a, _b, _c;
    const searchData = getLastSearch();
    const baseState = getBaseState(request, !!searchData);
    const inputrequest = {
        searchParameter: baseState,
        estabOffersRequest: createEstabOffersRequest(baseState, request),
    };
    if (!searchData)
        return inputrequest;
    try {
        const searchDetails = JSON.parse(searchData);
        const airportCodes = (_b = ((((_a = searchDetails.departureAirports) === null || _a === void 0 ? void 0 : _a.length) || 0) > 0
            ? searchDetails.departureAirports
            : baseState.departureAirports)) !== null && _b !== void 0 ? _b : [];
        const checkInDate = searchDetails.duration ? searchDetails.wholeMonthEndDate : searchDetails.startDate;
        const calendarMonthYear = getDealMonthAndYear(checkInDate !== null && checkInDate !== void 0 ? checkInDate : '', request.durationOffset);
        inputrequest.searchParameter = Object.assign(Object.assign({}, baseState), { departureAirports: airportCodes });
        inputrequest.estabOffersRequest = Object.assign(Object.assign({}, createEstabOffersRequest(baseState, request)), { duration: (_c = (searchDetails.dateSearchType === DateSearchType.ChooseMonth
                ? searchDetails.duration
                : getDayDiff(searchDetails.startDate, searchDetails.endDate))) !== null && _c !== void 0 ? _c : parseInt(request.duration), airportCodes: airportCodes !== null && airportCodes !== void 0 ? airportCodes : [], calendarMonthYear });
        return inputrequest;
    }
    catch (error) {
        console.error('Error creating estabOffer input request:', error);
        return inputrequest;
    }
}
